import {
  formatTimeAgo,
  getFullImageUrl,
  generateAvatarUrl,
} from "../utils/communityHelpers";

/**
 * Post Adapter
 * Maps API post responses to UI-expected format
 * Handles data transformation between backend and frontend structures
 */

/**
 * Map API post response to UI format
 * Transforms backend post structure to match UI expectations
 * @param {Object} apiPost - Post from API
 * @returns {Object} - Transformed post for UI
 */
export const mapPostFromAPI = (apiPost) => {
  try {
    if (!apiPost) return null;

    // Extract user information safely
    const userName = apiPost.user_name || apiPost.full_name || "Anonymous";
    const userImage = apiPost.user_image || apiPost.profile_photo_path;

    // Get first image from post_images array or null
    // IMPORTANT: Backend returns post_images as JSON string, need to parse it!
    let postImage = null;
    if (apiPost.post_images) {
      try {
        // Parse JSON string to array
        const imagesArray =
          typeof apiPost.post_images === "string"
            ? JSON.parse(apiPost.post_images)
            : apiPost.post_images;

        if (Array.isArray(imagesArray) && imagesArray.length > 0) {
          postImage = imagesArray[0]?.image_path;
        }
      } catch (error) {
        console.error("Error parsing post_images:", error, apiPost.post_images);
      }
    }

    // Extract comment users with their images (keep full user objects)
    // IMPORTANT: Backend returns latest_comment_users as JSON string, need to parse it!
    let commentedUsers = [];
    if (apiPost.latest_comment_users) {
      try {
        // Parse JSON string to array
        const usersArray =
          typeof apiPost.latest_comment_users === "string"
            ? JSON.parse(apiPost.latest_comment_users)
            : apiPost.latest_comment_users;

        if (Array.isArray(usersArray)) {
          commentedUsers = usersArray
            .map((user) => {
              if (!user) return null;
              const userName = user.user_name || "Unknown";
              const userImage = user.user_image;
              return {
                name: userName,
                // image: userImage ? getFullImageUrl(userImage) : userName,
                image: userImage ? userImage : userName,
              };
            })
            .filter(Boolean);
        }
      } catch (error) {
        console.error(
          "Error parsing latest_comment_users:",
          error,
          apiPost.latest_comment_users,
        );
      }
    }

    return {
      // Core fields
      id: apiPost.id,
      author: userName,
      title: apiPost.title || "",
      plan_id: apiPost.plan_id,
      plan_name: apiPost.plan_name,
      plan_slug: apiPost.plan_slug,

      // authorImage: userImage ? getFullImageUrl(userImage) : generateAvatarUrl(userName),
      authorImage: userImage ? userImage : userName,
      link_preview: apiPost.link_preview
        ? typeof apiPost.link_preview === "string"
          ? JSON.parse(apiPost.link_preview)
          : apiPost.link_preview
        : null,

      time: formatTimeAgo(apiPost.created_at),
      content: apiPost.content || "",
      image: postImage ? postImage : null,

      // Stats (use snake_case fields from API)
      // HYBRID APPROACH: Combine legacy likes (from mobile) + new reactions (from web)
      // This ensures cross-platform compatibility
      likes:
        parseInt((apiPost.total_reactions || 0) + (apiPost.likes_count || 0)) ||
        0,
      comments: apiPost.comments_count || 0,
      shares: 0, // Not supported by API

      // Category info
      categoryId: apiPost.category_id || "1",
      category: apiPost.category || "General",
      post_images: apiPost.post_images,
      categoryTag: apiPost.category || "General",

      // Like status - User has reacted if user_reaction exists
      // Supports both old isLiked field and new user_reaction field
      liked: Boolean(apiPost.user_reaction || apiPost.isLiked),

      // Follow / Save status — come straight from the posts API so we don't
      // have to hit /follow-status per card on render
      isFollowing: Boolean(apiPost.is_following),
      isSaved: Boolean(apiPost.is_saved),

      // Emoji reactions - Parse from backend API
      // Backend returns reactions as JSON string or object
      userReaction: apiPost.user_reaction || null,
      reactions: (() => {
        try {
          // Parse reactions from backend (may be JSON string)
          let reactionsData = apiPost.reactions;

          if (typeof reactionsData === "string") {
            reactionsData = JSON.parse(reactionsData);
          }

          // Ensure all reaction types exist with default 0
          return {
            like: parseInt(reactionsData?.like || 0) || 0,
            love: parseInt(reactionsData?.love || 0) || 0,
            haha: parseInt(reactionsData?.haha || 0) || 0,
            wow: parseInt(reactionsData?.wow || 0) || 0,
            sad: parseInt(reactionsData?.sad || 0) || 0,
            angry: parseInt(reactionsData?.angry || 0) || 0,
          };
        } catch (error) {
          console.error("Error parsing reactions:", error, apiPost.reactions);
          // Fallback to zeros if parsing fails
          return {
            like: 0,
            love: 0,
            haha: 0,
            wow: 0,
            sad: 0,
            angry: 0,
          };
        }
      })(),

      // Reaction details - Build from actual reaction data
      reactionDetails: (() => {
        try {
          let reactionsData = apiPost.reactions;
          if (typeof reactionsData === "string") {
            reactionsData = JSON.parse(reactionsData);
          }

          const details = [];
          const emojiMap = {
            like: "👍",
            love: "❤️",
            haha: "😂",
            wow: "😮",
            sad: "😢",
            angry: "😡",
          };

          Object.entries(reactionsData || {}).forEach(([type, count]) => {
            const numCount = parseInt(count) || 0;
            if (numCount > 0) {
              details.push({
                emoji: emojiMap[type] || "👍",
                count: numCount,
                users: apiPost.user_reaction === type ? ["You"] : [],
              });
            }
          });
          return details;
        } catch (error) {
          console.error("Error building reaction details:", error);
          return [];
        }
      })(),

      // Commented users preview
      commentedUsers: commentedUsers,

      // Liked users - Users who reacted to this post
      liked_users: apiPost.liked_users || [],

      // Comments list - Initially empty, loaded separately
      commentsList: [],

      // Repost fields
      is_repost: apiPost.is_repost || false,
      original_post: apiPost.original_post || null,
      original_post_id: apiPost.original_post_id || null,
      repost_comment: apiPost.repost_comment || null,

      // Embedded poll (null if post has no poll)
      poll: apiPost.poll || null,

      // Additional metadata (for internal use)
      _metadata: {
        userId: apiPost.user_id,
        userState: apiPost.user_state,
        userCity: apiPost.user_city,
        userRole: apiPost.user_role,
        userPrimarySpecialty: apiPost.user_primary_specialty,
        userAdditionalSpecialties: apiPost.user_additional_specialties,
        isAnonymous: apiPost.is_post_anonymously,
        postCategoryId: apiPost.post_category_id,
        createdAt: apiPost.created_at,
        updatedAt: apiPost.updated_at,
        engagementRate: apiPost.post_engagement_rate || 0,
      },
    };
  } catch (error) {
    console.error("Error mapping post from API:", error, apiPost);
    return null;
  }
};

/**
 * Map array of API posts to UI format
 * @param {Array} apiPosts - Posts array from API
 * @returns {Array} - Transformed posts for UI
 */
export const mapPostsFromAPI = (apiPosts) => {
  try {
    if (!Array.isArray(apiPosts)) {
      console.warn("mapPostsFromAPI: Expected array, got:", typeof apiPosts);
      return [];
    }

    return apiPosts.map(mapPostFromAPI).filter(Boolean); // Filter out null values
  } catch (error) {
    console.error("Error mapping posts from API:", error);
    return [];
  }
};

/**
 * Map like/unlike API response to update post state
 * @param {Object} apiResponse - Response from like/unlike endpoint
 * @returns {Object} - Updated like data
 */
export const mapLikeResponseFromAPI = (apiResponse) => {
  try {
    if (!apiResponse?.data) return null;

    return {
      likeCount: apiResponse.data.like_count || 0,
      liked: apiResponse.data.liked ?? false,
    };
  } catch (error) {
    console.error("Error mapping like response from API:", error);
    return null;
  }
};

/**
 * Update post object with new like data
 * Merges like response into existing post object
 * @param {Object} post - Current post object
 * @param {Object} likeData - Like data from API
 * @returns {Object} - Updated post object
 */
export const updatePostWithLikeData = (post, likeData) => {
  try {
    if (!post || !likeData) return post;

    return {
      ...post,
      likes: likeData.likeCount,
      liked: likeData.liked,
      userReaction: likeData.liked ? "like" : null,
      reactions: {
        ...post.reactions,
        like: likeData.likeCount,
      },
      reactionDetails:
        likeData.likeCount > 0
          ? [
              {
                emoji: "👍",
                count: likeData.likeCount,
                users: likeData.liked ? ["You"] : [],
              },
            ]
          : [],
    };
  } catch (error) {
    console.error("Error updating post with like data:", error);
    return post;
  }
};

/**
 * Update post object with new comment count
 * @param {Object} post - Current post object
 * @param {number} newCommentCount - Updated comment count
 * @returns {Object} - Updated post object
 */
export const updatePostWithCommentCount = (post, newCommentCount) => {
  try {
    if (!post) return post;

    return {
      ...post,
      comments: newCommentCount,
    };
  } catch (error) {
    console.error("Error updating post with comment count:", error);
    return post;
  }
};

/**
 * Map new post API response to UI format
 * Used when creating a new post
 * Backend may not return full user data, so we accept it as parameters
 * @param {Object} apiResponse - Response from create post endpoint
 * @param {Object} userData - User data { name, image, isAnonymous } from Redux
 * @param {string} categoryName - Category name from form
 * @returns {Object} - Transformed post for UI
 */
export const mapNewPostFromAPI = (
  apiResponse,
  userData = null,
  categoryName = null,
) => {
  try {
    // Handle multiple response structures:
    // 1. response.data.data.post (nested in post object)
    // 2. response.data.post (directly in data)
    // 3. response.data.data (directly as post data)
    const post =
      apiResponse?.data?.data?.post ||
      apiResponse?.data?.post ||
      apiResponse?.data?.data;

    if (!post || !post.id) {
      console.warn(
        "[mapNewPostFromAPI] No valid post in response:",
        apiResponse,
      );
      return null;
    }

    // Get first image from post_images array or direct image_path
    // CRITICAL: Backend returns post_images as JSON string, need to parse it!
    let postImage = null;

    if (post.post_images) {
      try {
        console.log(
          "[mapNewPostFromAPI] post_images type:",
          typeof post.post_images,
        );
        console.log(
          "[mapNewPostFromAPI] post_images raw value:",
          post.post_images,
        );

        // Parse JSON string to array if needed
        let imagesArray;
        if (typeof post.post_images === "string") {
          console.log("[mapNewPostFromAPI] Parsing string to array...");
          imagesArray = JSON.parse(post.post_images);
        } else if (Array.isArray(post.post_images)) {
          console.log("[mapNewPostFromAPI] Already an array");
          imagesArray = post.post_images;
        } else {
          console.log(
            "[mapNewPostFromAPI] Unknown type, treating as array with single item",
          );
          imagesArray = [post.post_images];
        }

        console.log(
          "[mapNewPostFromAPI] Parsed post_images array:",
          imagesArray,
        );

        if (Array.isArray(imagesArray) && imagesArray.length > 0) {
          const firstImage = imagesArray[0];
          console.log("[mapNewPostFromAPI] First image object:", firstImage);

          // Try multiple possible property names
          postImage =
            firstImage?.image_path ||
            firstImage?.image_url ||
            firstImage?.path ||
            firstImage?.url ||
            firstImage;

          console.log("[mapNewPostFromAPI] Extracted image path:", postImage);

          // If postImage is still an object, log it
          if (typeof postImage === "object") {
            console.error(
              "[mapNewPostFromAPI] Image is still an object! Keys:",
              Object.keys(postImage),
            );
            console.error("[mapNewPostFromAPI] Object value:", postImage);
          }
        }
      } catch (error) {
        console.error("[mapNewPostFromAPI] Error parsing post_images:", error);
        console.error("[mapNewPostFromAPI] Raw value was:", post.post_images);
      }
    }

    // Fallback to direct properties
    if (!postImage && post.image_path) {
      postImage = post.image_path;
      console.log("[mapNewPostFromAPI] Image from image_path:", postImage);
    } else if (!postImage && post.image_url) {
      postImage = post.image_url;
      console.log("[mapNewPostFromAPI] Image from image_url:", postImage);
    }

    if (!postImage) {
      console.error("[mapNewPostFromAPI] ❌ NO IMAGE FOUND IN API RESPONSE!");
      console.error("[mapNewPostFromAPI] post_images value:", post.post_images);
      console.error(
        "[mapNewPostFromAPI] post_images type:",
        typeof post.post_images,
      );
    }

    // Use provided user data or fallback to API response or placeholder
    let author, authorImage;

    if (userData) {
      // Use the provided user data (from optimistic post)
      author = userData.isAnonymous ? "Anonymous" : userData.name || "You";
      authorImage = userData.image;
      // authorImage = userData.isAnonymous
      //   ? generateAvatarUrl('Anonymous')
      //   : (userData.image || generateAvatarUrl(userData.name || 'You'));
    } else {
      // Fallback to API response or placeholder
      const userName = post.user_name || post.full_name || "You";
      const userImage = post.user_image || post.profile_photo_path;
      author = userName;
      authorImage = userImage ? userImage : userName;
      // authorImage = userImage
      //   ? getFullImageUrl(userImage)
      //   : generateAvatarUrl(userName);
    }

    // Use provided category name or fallback to API response or 'General'
    const finalCategory = categoryName || post.category || "General";

    return {
      id: post.id,
      author: author,
      title: post.title || "",
      authorImage: authorImage,
      time: "Just now",
      content: post.content || "",
      image: postImage ? postImage : null,
      likes: 0,
      comments: 0,
      shares: 0,
      category: post.post_category_id,
      categoryTag: finalCategory,
      post_images: [
        {
          id: 208,
          image_path: "uploads/post_images/1762836118195_2WT.png",
        },
      ],
      liked: false,
      userReaction: null,
      reactions: {
        like: 0,
        love: 0,
        haha: 0,
        wow: 0,
        angry: 0,
      },
      reactionDetails: [],
      commentedUsers: [],
      liked_users: [], // Empty for new posts
      commentsList: [],
      poll: null,
      _optimistic: false, // Real post from server
      _metadata: {
        userId: post.user_id,
        userState: post.user_state,
        isAnonymous: post.is_post_anonymously,
        postCategoryId: post.post_category_id,
        createdAt: post.created_at,
        updatedAt: post.updated_at,
        engagementRate: 0,
      },
    };
  } catch (error) {
    console.error("[mapNewPostFromAPI] Error:", error, apiResponse);
    return null;
  }
};
